import { CircleCheck, Info, LoaderCircle, OctagonX, TriangleAlert } from 'lucide-react'
import { Toaster as Sonner } from 'sonner'
import { useTheme } from '@/hooks/use-theme'

type ToasterProps = React.ComponentProps<typeof Sonner>

/**
 * Toaster — sonner integration with the Caliber theme hook.
 *
 * Diverges from the shadcn default (which imports `next-themes`) by reading
 * theme from the project's own `useTheme` hook so light/dark mode stays in
 * lockstep with the rest of the app (Story 2.2 NFR-DS-1).
 *
 * Token classes (`bg-background`, `text-foreground`, `border-hair`,
 * `bg-muted`, `text-muted-foreground`, `bg-primary`, `text-primary-foreground`)
 * resolve via the shadcn alias block added to `globals.css` in this story.
 *
 * ⚠️ ESTE TOAST NÃO PODE ADOTAR A `@utility glass-float` A PARTIR DAQUI, e as
 * classes de superfície que aqui estão JÁ NÃO PINTAM NADA. Não é opinião — foi
 * medido, e a causa é a cascata por CAMADAS:
 *
 *   O sonner injeta o seu CSS em runtime (`document.head.appendChild(<style>)`,
 *   ver `__insertCSS` no `dist/index.mjs`), portanto SEM CAMADA. O CSS do
 *   Tailwind vive em `@layer utilities` (posto lá pelo `@import 'tailwindcss'`,
 *   e é lá que o `@utility` também aterra). Pela spec, declarações SEM camada
 *   vencem QUALQUER camada, com qualquer especificidade. Logo
 *   `[data-sonner-toast][data-styled='true'] { background; border; box-shadow }`
 *   ganha sempre a um utilitário — variante ou não.
 *
 *   Medido em Chrome com o cenário reproduzido à letra (utilitário em
 *   `@layer utilities` vs `<style>` sem camada injetado depois): o `background`
 *   computado é o do sonner. O `backdrop-filter` é o único que passa, porque o
 *   sonner não o declara — ou seja, o vidro ficaria com o blur POR BAIXO de uma
 *   tinta opaca: invisível. É a mesma classe de defeito do `bg-panel-hover` no
 *   combobox — a classe existe, gera CSS válido, e o resultado não aparece.
 *
 * Consequência a registar: `group-[.toaster]:bg-background`, `:border-hair` e
 * `:shadow-lg` abaixo são INERTES hoje. O toast está pintado pelo `--normal-bg`
 * do próprio sonner, não pelos tokens do Caliber. Ficam porque removê-las não
 * muda um pixel e a decisão de as trocar é de quem puder mexer no `globals.css`.
 *
 * ⚠️ As duas saídas óbvias NÃO servem, e ambas foram testadas:
 *   - a utility com o modificador `!` do Tailwind (escrito aqui por extenso de
 *     propósito: o scanner do Tailwind lê comentários, e a forma literal geraria
 *     no bundle uma regra que ninguém usa). O `!` marca TODAS as declarações da
 *     utility, incluindo `position: relative`, e o sonner precisa de
 *     `position: absolute` em `[data-sonner-toast]` para empilhar os toasts.
 *     Ganhava a cor e partia o layout.
 *   - `toastOptions.unstyled` — apaga `data-styled='true'`, e com ele as 18
 *     regras que estilam título, descrição, ícone, botões, botão de fechar e o
 *     esmaecer da pilha. Era reconstruir o toast inteiro.
 *
 * A saída real vive no `globals.css`: uma regra FORA de camada para
 * `[data-sonner-toast][data-styled='true']` (com `@apply glass-float` para não
 * duplicar o material), mais o `position: absolute` reposto — porque essa regra,
 * sendo mais específica que a do sonner, também levaria o `position: relative`
 * da utility.
 */
export function Toaster(props: ToasterProps) {
  const { theme } = useTheme()
  // Story 10.1 added an 'auto' choice; sonner's equivalent is 'system' (it
  // follows prefers-color-scheme natively), so the toaster tracks the OS like
  // the rest of the app does while in auto.
  const sonnerTheme: ToasterProps['theme'] = theme === 'auto' ? 'system' : theme

  return (
    <Sonner
      theme={sonnerTheme}
      className="toaster group"
      icons={{
        success: <CircleCheck className="h-4 w-4" />,
        info: <Info className="h-4 w-4" />,
        warning: <TriangleAlert className="h-4 w-4" />,
        error: <OctagonX className="h-4 w-4" />,
        loading: <LoaderCircle className="h-4 w-4 animate-spin" />,
      }}
      toastOptions={{
        classNames: {
          // ⚠️ INERTE — ver o docblock. Sem camada > com camada, e o sonner
          // injeta sem camada. Não substituir por `glass-float` sem primeiro
          // arranjar a regra fora de camada no `globals.css`.
          toast:
            'group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-hair group-[.toaster]:shadow-lg',
          description: 'group-[.toast]:text-muted-foreground',
          actionButton: 'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground',
          cancelButton: 'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground',
        },
      }}
      {...props}
    />
  )
}
